import React, { forwardRef, useEffect, useState, useRef, useImperativeHandle } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import {
  CircularProgress,
  SvgIcon,
  FormGroup,
  Button,
  Tooltip,
  createTheme,
  ThemeProvider
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { sub } from 'date-fns';
import { QuoteProductID } from '../../Common/QuoteProductID';
import QuoterFilter from '../../CustomizeTerm/Helpers/QuoterFilter';
import { GetMCTQuotes } from '../../CustomizeTerm/Common/GetMCTQuotes';
import Age from '../../../Helpers/Dates/Age';
import { getTermLengthFromAge } from '../../../Helpers/Lists/CoverageLengths/AmericoCoverageLengths';
import { getFaceAmountFromAge } from '../../../Helpers/Lists/CoverageAmounts/AmericoCoverageAmounts';
import { customValidators, object, string, date } from '../../../Helpers/Validators/customValidators';
import States from '../../../Helpers/Lists/States';
import StatesWithDisabled from '../../../Helpers/Lists/StatesWithDisabled';
import TobaccoUseExtended from '../../../Helpers/Lists/TobaccoUseExtended';
import { InputMaskHeight, InputMaskWeight, InputMaskDate } from '../../../Helpers/Formatters';
import { TextInput, SelectInput, StyledRadioInput } from '../../../Helpers/Inputs';
import UserIcon from '../../../../icons/user.svg?react';
import {
  getBasicInfo,
  setBasicInfo,
  setSessionStorageItem,
  getSessionStorageItem,
  SESSION_STORAGE_ACCOUNT_KEY,
  SESSION_STORAGE_SAVED_QUOTE_DATA,
  SESSION_STORAGE_TERM_SORT_FILTER_KEY
} from '../../../../sessionStorage';
import ShieldCheckmarkIcon from '../../../../icons/shield-checkmark.svg?react';
import TestimonialPanel from '../../../Common/Testimonials/TestimonialPanel';
import './AboutMe.scss';

const termSettings = getSessionStorageItem(SESSION_STORAGE_TERM_SORT_FILTER_KEY);

const AboutMeForm = forwardRef((props, ref) => {
  const innerRef = useRef(null);
  const { isPartnerReferralEmailCaptureActive, enablePartnerReferralEmailCapture } = props.emailCapture;

  const theme = createTheme({
    palette: {
      primary: {
        main: `${props.brandTheme ? props.brandTheme : "#3f51b5"}`,
      },
    },
    typography: {
      fontFamily: "museo-sans",
      body1: {
        fontSize: 14,
        fontWeight: 500,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            "&.MuiButton-contained.MuiButton-colorPrimary": {
              color: 'rgba(0, 0, 0, 0.87)',
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            fontSize: 16,
          },
          root: {
            height: 56,
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: "rgba(0, 0, 0, 0.05)",
          },
          underline: {
            "&::before": {
              borderBottom: "solid 2px #b2b2b2",
            },
            "&:hover::before": {
              borderBottomWidth: "2px",
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: 16,
            color: "rgba(0,0,0,0.6)",
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginTop: 0,
            paddingTop: 0,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.Mui-error': {
              fontWeight: 400,
              fontSize: '17px',
            },
          },
          shrink: {
            color: "rgba(0,0,0,0.3) !important",
          },
        },
      },
      MuiNativeSelect: {
        styleOverrides: {
          fontSize: 16,
          icon: {
            color: "rgba(0,0,0,1)",
          },
          iconFilled: {
            right: 16,
          },
        },
      },
    },
  });

  const basicInfo = getBasicInfo();
  if (basicInfo && basicInfo.state && (props.unavailableStates && props.unavailableStates.includes(basicInfo.state))) {
    basicInfo.state = '';
  }
  const [formState, setFormState] = useState(AboutMeForm.DefaultFormState(false, basicInfo));
  const age = Age(formState.birthdate);
  const coverageAmountAsPerAge = (basicInfo && basicInfo.faceAmount) || getFaceAmountFromAge(age);
  const termlenghtAsPerAge = (basicInfo && basicInfo.termLength) || getTermLengthFromAge(age);

  const accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
  const savedQuote = getSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA);
  const accountProductTypeSettings = props ? accountObj.accountProducts.find(product => product.productLandingUrl === '/landing/term-insurance' || product.productLandingUrl === '/landing/loan-protection') : null;

  const [loading, setLoading] = useState(false);
  const [teaserRate, setTeaserRate] = useState(null);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [filterObject, setFilterObject] = useState((termSettings !== null && termSettings.filters !== null) ? termSettings.filters : null);
  const [termLength, setTermLength] = useState(20);
  const [coverageAmount, setCoverageAmount] = useState(500000);
  const [healthClass, setHealthClass] = useState('PP');
  const [filteredQuoteResults, setFilteredQuoteResults] = useState([]);
  const [americoQuoteResult, setAmericoQuoteResult] = useState([]);
  const [fastestRate, setFastestRate] = useState('');
  const [lowestPrice, setlowestPrice] = useState('');

  const [quoteResults, setQuoteResults] = useState([]);
  const [quoteLowestPrice, setQuoteLowestPrice] = useState(null);
  const [quoteFastestProcess, setQuoteFastestProcess] = useState(null);
  const [quoteMostPopular, setQuoteMostPopular] = useState(null);
  const [saveQuoteData, setSaveQuoteData] = useState(null);
  const [saveQuoteContact, setSaveQuoteContact] = useState(savedQuote);
  const [isQuoteSaved, setQuoteSaved] = useState(savedQuote.quoteSaved);

  const [tooltip, setTooltip] = useState(false);
  const handleTooltipAboutClose = () => {
    setTooltip(false);
  };
  const handleTooltipAboutOpen = () => {
    setTooltip(true);
  };
  const formMethods = useForm({
    resolver: yupResolver(AboutMeForm.ValidationSchema(props.ageSpecs)),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: formState,
  });

  const { handleSubmit, trigger } = formMethods;

  useEffect(() => {
    if (saveQuoteContact.emailAddress !== "") {
      setSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA, {
        "quoteSaved": isQuoteSaved,
        "firstName": saveQuoteContact.firstName,
        "lastName": saveQuoteContact.lastName,
        "emailAddress": saveQuoteContact.emailAddress,
      });
    }
    setCurrentSavedQuoteData();
  }, [isQuoteSaved]);

  const sorter = (a, b, sortByField) => {
    switch (sortByField) {
      case 'lowestPrice':
        return a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore;
      case 'highestPrice':
        return b.monthlyRate - a.monthlyRate || a.totalUnweightedScore - b.totalUnweightedScore;
      case 'speed':
        return b.speedScore - a.speedScore || a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore;
      case 'bestSellers':
        return
      case 'highestRated':
        return b.companyRatingsScore - a.companyRatingsScore || a.totalUnweightedScore - b.totalUnweightedScore;
      case 'fastest':
        return b.speedScore - a.speedScore || a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore;
      default:
        break;
    }
  };

  const filterResults = (quotes) => {
    return QuoterFilter.filterResults(filterObject, quotes);
  };

  useEffect(() => {
    if (quoteResults.length > 0) {
      quoteResults.sort((a, b) => b.speedScore - a.speedScore || a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore)[0];
      quoteResults[0].isFastest = true;
      setFastestRate(quoteResults[0].monthlyRate);
      quoteResults.sort((a, b) => a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore)[0];
      setlowestPrice(quoteResults[0].monthlyRate);
      quoteResults[0].isBestPrice = true;

      quoteResults.sort((a, b) => sorter(a, b, (!!props.quoteResultSortDefault ? props.quoteResultSortDefault : 'lowestPrice')));
      const filteredQuotes = filterResults(quoteResults);
      setFilteredQuoteResults(filteredQuotes);

    } else {
      setFilteredQuoteResults([]);
    }

    if (quoteResults.length === 0) {
      return;
    }
    else {
      const minRate = quoteResults[0].monthlyRate;
      setTeaserRate(minRate);
    }

  }, [quoteResults]);

  const getQuoteFastestProcess = (results, lowest) => {
    const sortedResults = results.sort((a, b) => a.speedScore - b.speedScore);
    const product = results.find(product => !!product.isFastest);

    if (product && lowest && product.productID === lowest.productID) {
      return sortedResults[sortedResults.length - 2];
    }
    return product;
  };

  const getQuoteMostPopular = (results, fastest, lowest) => {
    const productIds = [QuoteProductID.LGA_PRODUCT_ID, QuoteProductID.BANNER_OPTERM_PRODUCT_ID, QuoteProductID.PENN_OPTERM_PRODUCT_ID, QuoteProductID.SYMETRA_SWIFT_TERM_PRODUCT_ID, QuoteProductID.PACIFIC_LIFE_PROMISE_TERM_PRODUCT_ID, QuoteProductID.AMERICO_TERM_PRODUCT_ID];

    if (!props.isAmerico && fastest && lowest) {
      let product = null;

      for (const id of productIds) {
        product = results.find(product => product.productID === id && fastest.productID !== id && lowest.productID !== id);
        if (product) {
          break;
        }
      }
      return product;
    }
    if (props.isAmerico && fastest && lowest) {
      let product = null;

      product = results.find(product => product.productID === QuoteProductID.AMERICO_TERM_PRODUCT_ID && fastest.productID !== QuoteProductID.AMERICO_TERM_PRODUCT_ID && lowest.productID !== QuoteProductID.AMERICO_TERM_PRODUCT_ID);

      if (product) {
        return product;
      } else {

        for (const id of productIds) {
          product = results.find(product => product.productID === id && fastest.productID !== id && lowest.productID !== id);
          if (product) {
            break;
          }
        }
        return product;
      }
    }
  };

  const setQuoteValues = () => {
    const quotedLowestPrice = quoteResults.find(product => !!product.isBestPrice);
    const quotedFastestProcess = getQuoteFastestProcess(quoteResults, quotedLowestPrice);
    const quotedMostPopular = getQuoteMostPopular(quoteResults, quotedFastestProcess, quotedLowestPrice);

    const lowestPriceOdj = {
      carrierLogo: quotedLowestPrice && quotedLowestPrice.logoUrl,
      rating: quotedLowestPrice && quotedLowestPrice.rating,
      monthlyRate: quotedLowestPrice && quotedLowestPrice.monthlyRate && quotedLowestPrice.monthlyRate.toFixed(2),
    };

    const fastestProcessOdj = {
      carrierLogo: quotedFastestProcess && quotedFastestProcess.logoUrl,
      rating: quotedFastestProcess && quotedFastestProcess.rating,
      monthlyRate: quotedFastestProcess && quotedFastestProcess.monthlyRate && quotedFastestProcess.monthlyRate.toFixed(2),
    };

    const mostPopularOdj = {
      carrierLogo: quotedMostPopular && quotedMostPopular.logoUrl,
      rating: quotedMostPopular && quotedMostPopular.rating,
      monthlyRate: quotedMostPopular && quotedMostPopular.monthlyRate && quotedMostPopular.monthlyRate.toFixed(2),
    };

    setQuoteLowestPrice(lowestPriceOdj);
    setQuoteFastestProcess(fastestProcessOdj);
    setQuoteMostPopular(mostPopularOdj);
  };

  const setCurrentSavedQuoteData = () => {
    const newData = getSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA);
    setSaveQuoteContact(newData);
  };

  const trimString = (str) => {
    if (str !== null) {
      return str?.replace(/\s+/g, " ");
    }
    return "";
  };

  useEffect(() => {
    setSaveQuoteContact(props.saveQuoteContact);
  }, [props.saveQuoteContact]);

  useEffect(() => {
    if (quoteResults.length > 0) {
      setQuoteValues();
    }
  }, [loading]);

  useEffect(() => {
    props.setEmailCaptureActive(submitDisabled);
  }, [submitDisabled]);

  const covrDisclaimer = 'Insurance products are sold through Covr Financial Technologies Inc ("Covr"). Covr sells insurance as an agent or broker by contractual agreements with its insurance carrier providers. Covr is licensed in the following states: Delaware (3000067942), Idaho (607086), California (0L77229).';

  useEffect(() => {
    const quoteData = {
      accountId: accountObj.account.accountId,
      accountName: accountObj.account.accountName,
      oneClickAccountId: accountObj.account.oneClickAccountId,
      accountProductTypeSettingId: accountProductTypeSettings.accountProductTypeSettingId,
      productType: "loanprotector",
      sessionId: accountObj.sessionId,
      accountColorLogoUrl: accountObj.account.logoUrl,
      firstName: saveQuoteContact.firstName,
      lastName: saveQuoteContact.lastName,
      emailAddress: saveQuoteContact.emailAddress,
      gender: basicInfo.gender,
      dateOfBirth: basicInfo.birthdate,
      state: basicInfo.state,
      height: trimString(basicInfo.height),
      weight: trimString(basicInfo.weight),
      tobaccoStatus: (basicInfo.tobacco !== 'Never' && basicInfo.tobacco !== 'false'),
      coverageAmount: coverageAmount,
      termLength: termLength,
      healthClass: healthClass,
      quoteLowestPrice: quoteLowestPrice,
      quoteFastestProcess: quoteFastestProcess,
      quoteMostPopular: quoteMostPopular,
      quotedLowestPrice: !!quoteLowestPrice ? quoteLowestPrice.monthlyRate : null,
      signatureEmail: "insuranceapplication@covrtech.com",
      signaturePhone: accountObj.account.phone,
      covrDisclaimer: covrDisclaimer,
      disclaimerHtml: accountObj.account.disclaimerHtml ? accountObj.account.disclaimerHtml : null,
    };
    setSaveQuoteData(quoteData);
  }, [quoteLowestPrice, quoteFastestProcess, quoteMostPopular, termLength, coverageAmount, healthClass, saveQuoteContact]);

  useEffect(() => {
    props.setSaveQuoteData(saveQuoteData);
  }, [saveQuoteData]);

  const runQuote = (getIsSubscribed) => {
    const coverageAmount = coverageAmountAsPerAge;
    const termLength = termlenghtAsPerAge;
    let healthClass = 'PP';

    if (props.teaserQuoteParams && props.teaserQuoteParams.healthClass) {
      healthClass = props.teaserQuoteParams.healthClass;
    }

    GetMCTQuotes(formState, healthClass, coverageAmount.toString(10), termLength.toString(10), setLoading, getIsSubscribed, null, setQuoteResults, ['Level'], null, null, false, true);
  };

  const isValidHeight = (height) => {
    if (height) {
      const feet = height.split('ft')[0].trim();
      const inches = height.split('ft')[1].split('in')[0].trim();
      if (feet > 0 && inches >= 0 && inches !== "") {
        return true;
      }
      return false;

    }
    return false;
  };

  const isValidWeight = (weight) => {
    const wts = parseInt(weight);
    if (weight && wts >= 78 && wts <= 395) {
      const wt = weight.split(' ')[0].trim();
      if (wt) {
        return true;
      }
      return false;

    }
    return false;
  };

  const isValidDate = (birthdate) => {
    const date = new Date(birthdate);
    const age = Age(birthdate);
    return (date !== "Invalid Date" && age <= props.ageSpecs.max && age >= props.ageSpecs.min);
  };

  const inValidBasicInfo = () => {
    if (formState.gender && formState.state && formState.tobacco
      && isValidHeight(formState.height) && isValidWeight(formState.weight) && isValidDate(formState.birthdate)) {
      return true;
    }
    return false;
  };

  let debounce = null;
  const GetQuotes = () => {
    if ((props.term30EnableTeaserRate || !!isPartnerReferralEmailCaptureActive) && inValidBasicInfo()) {
      setLoading(true);
      let isSubscribed = true;
      if (debounce) {
        clearTimeout(debounce);
      }
      debounce = setTimeout(() => runQuote(() => isSubscribed), 1000);
      return () => {
        clearTimeout(debounce);
        isSubscribed = false;
      };
    }
  };

  useEffect(() => {
    setSubmitDisabled(!!isPartnerReferralEmailCaptureActive && !!enablePartnerReferralEmailCapture);
    props.setEmailCaptureActive(submitDisabled);

    if (!props.hideCoverage || !!isPartnerReferralEmailCaptureActive) {
      GetQuotes();
    }
  }, []);

  useEffect(() => {
    GetQuotes();
  }, [formState.gender, formState.birthdate, formState.state, formState.height, formState.weight, formState.tobacco]);

  const handleValueChange = (field, value) => {
    setFormState({ ...formState, [field]: value });
    if (field === 'gender' || field === 'tobacco') {
      trigger(field);
    }
  };

  function calculateTotalHeight() {
    const radix = 10;
    const heightArray = formState.height.split(" ");
    const heightFeet = parseInt(heightArray[0], radix);
    let heightInches;
    for (let i = 1; i < heightArray.length; i++) {
      if (heightArray[i] !== "" && heightArray[i] !== "ft") {
        heightInches = parseInt(heightArray[i], radix);
        break;
      }
    }
    ;
    return heightFeet * 12 + heightInches;
  };

  useImperativeHandle(ref, () => ({
    onSubmit,
    dispatchSubmit: () => {
      innerRef.current?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
    get element() {
      return innerRef.current;
    }
  }));

  const onSubmit = () => {
    const totalHeight = calculateTotalHeight();
    const weight = formState.weight.split(" ")[0];

    if (props.validateBMI != null && props.validateBMI(totalHeight, formState.bmi, weight) && props.onDeclined != null) {
      props.onDeclined();
    } else {
      // Save formState to basicinfo
      const basicInfoFromSessionStorage = getBasicInfo();
      basicInfoFromSessionStorage.gender = formState.gender;
      basicInfoFromSessionStorage.birthdate = formState.birthdate;
      basicInfoFromSessionStorage.state = formState.state;
      basicInfoFromSessionStorage.height = formState.height;
      basicInfoFromSessionStorage.weight = formState.weight;
      basicInfoFromSessionStorage.tobacco = formState.tobacco;
      basicInfoFromSessionStorage.usResident = formState.usResident;

      setBasicInfo(basicInfoFromSessionStorage);

      if (submitDisabled && isPartnerReferralEmailCaptureActive) {
        props.setModalSaveQuoteIsOpen(submitDisabled);
      }

      props.onNext();
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div className={`basic-info-about-me-loan-protection`}>
        <FormProvider {...formMethods}>
          <Form onSubmit={handleSubmit(onSubmit)} ref={innerRef}>
            <Row>
              <Col lg={12}>
                {props.showHeader && (
                  <React.Fragment>
                    <Row className="justify-content-center">
                      <SvgIcon color='primary' component={UserIcon} className="icons" fontSize="large" />
                    </Row>
                    <Row className="justify-content-center">
                      <h3 className="py-3">Profile</h3>
                      <Tooltip
                        title="This information should represent the person who will be insured and covered by the policy, whether that's you or someone else."
                        id="about-tooltip" aria-describedby="about-tooltip" aria-label="more about sort by price"
                        className="toolInfo" role="tooltip"
                        enterTouchDelay={0} leaveTouchDelay={5000}
                        onClose={() => handleTooltipAboutClose()}
                        open={tooltip}
                        slotProps={{
                          tooltip: {
                            className: 'light-tooltip',
                            sx: { fontSize: '11px' }
                          },
                        }}>
                        <button type='button' id="about" role="tooltip" className="quote-tooltip"
                          onMouseOver={() => handleTooltipAboutOpen()} onClick={() => handleTooltipAboutOpen()}
                          tabIndex="0" aria-disabled="true" aria-describedby="about"><InfoIcon
                            color="action" /></button>
                      </Tooltip>
                    </Row>
                  </React.Fragment>
                )}
                <Row>
                  <Col>
                    <FormGroup>
                      <StyledRadioInput
                        name="gender"
                        id="gender"
                        label=""
                        value={formState.gender}
                        options={[
                          { value: "F", label: "Female", icon: 'female' },
                          { value: "M", label: "Male", icon: 'male' },
                        ]}
                        onChange={e => handleValueChange('gender', e)}
                        isBlankIcon={true}
                        isBlankCheckMark={false}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row className='mt-2'>
                  <Col>
                    <div className='mb-2'>
                      <TextInput
                        name="birthdate"
                        id="birthdate"
                        label="Date of Birth"
                        value={formState.birthdate}
                        onChange={(e) => handleValueChange('birthdate', e.target.value)}
                        InputProps={{
                          inputComponent: InputMaskDate,
                        }}
                      />
                    </div>
                  </Col>
                </Row>
                <Row className='mt-2'>
                  <Col>
                    <div className='mb-2'>
                      <SelectInput
                        name="state"
                        id="state"
                        value={formState.state}
                        onChange={(e) => handleValueChange('state', e.target.value)}
                        label="State"
                        selectOptions={props.unavailableStates ? StatesWithDisabled(props.unavailableStates) : States()}
                      />
                    </div>
                  </Col>
                </Row>
                <Row className='mt-2'>
                  <Col>
                    <div className='mb-2'>
                      <TextInput
                        name="height"
                        id="height"
                        label="Height"
                        value={formState.height}
                        onChange={(e) => handleValueChange('height', e.target.value)}
                        InputProps={{
                          inputComponent: InputMaskHeight,
                        }}
                      />
                    </div>
                  </Col>
                  <Col>
                    <div className='mb-2'>
                      <TextInput
                        name="weight"
                        id="weight"
                        label="Weight"
                        value={formState.weight}
                        onChange={(e) => handleValueChange('weight', e.target.value)}
                        InputProps={{
                          inputComponent: InputMaskWeight,
                        }}
                      />
                    </div>
                  </Col>
                </Row>
                <Row className='mt-2'>
                  <Col>
                    <div className='mb-2'>
                      <SelectInput
                        name="tobacco"
                        id="tobacco"
                        value={formState.tobacco}
                        onChange={(e) => handleValueChange('tobacco', e.target.value)}
                        label="Tobacco/Nicotine Use"
                        selectOptions={props.tobaccoOptions || TobaccoUseExtended()}
                        aria-invalid="true"
                        aria-describedby="state"
                      />
                    </div>
                  </Col>
                </Row>
              </Col>
              {props.showQuoteButton &&
                <Col xs={12}>
                  <Button variant="contained" color="primary" type="submit" fullWidth className='submit-button'>
                    <ShieldCheckmarkIcon style={{ width: "20px" }} className='me-1' />UPDATE MY QUOTE</Button>
                </Col>
              }
            </Row>
            {props.quickQuoteDisclaimer && props.submitButtonVariant === 'landing' &&
              <Row>
                <Col className="quick-quote-disclaimer-container"
                  dangerouslySetInnerHTML={{ __html: props.quickQuoteDisclaimer }} />
              </Row>
            }

            <Row className="mt-0">
              {!props.submitButtonVariant && !props.hideNextButton &&
                <Col className="text-end pb-3">
                  <Button variant="contained" size="medium" color="primary" type="submit">
                    NEXT
                  </Button>
                </Col>
              }
              {props.submitButtonVariant && props.submitButtonVariant === 'landing' &&
                <Col className="d-flex mt-3">
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth
                    className='submit-button'
                    disabled={loading && isPartnerReferralEmailCaptureActive}>
                    {(loading && isPartnerReferralEmailCaptureActive) ? <CircularProgress style={{ marginRight: 15 }} color="inherit" size={20} />
                      : <ShieldCheckmarkIcon className="button-icon" />} SEE YOUR QUOTES
                  </Button>
                  {/* <Button variant="contained" color="primary" type="submit" fullWidth className='submit-button'>
                      <ShieldCheckmarkIcon className="button-icon"/>SEE YOUR QUOTE</Button> */}
                </Col>
              }
            </Row>
            {props.showTestimonialPanel && <TestimonialPanel />}
          </Form>
        </FormProvider>
      </div>
    </ThemeProvider>
  );
});

AboutMeForm.ValidationSchema = (ageSpecs) => {
  return object().shape({
    gender: string().required('Gender is required.'),
    birthdate: date()
      .typeError('Invalid date.')
      .max(sub(new Date(), {
        years: ageSpecs.min,
        days: ageSpecs.minAgeDays || 0,
      }), `Minimum age is ${ageSpecs.minAgeDays ? `${ageSpecs.minAgeDays} days` : ageSpecs.min}.`)
      .min(sub(new Date(), { years: ageSpecs.max + 1 }), `Maximum age is ${ageSpecs.max}.`)
      .required('Birthdate is required.'),
    state: string().required('State is required.'),
    height: customValidators.string()
      .required('Height is required.')
      .isValidEtradeHeightString('Invalid height', 36, 84),
    weight: customValidators.string()
      .required('Weight is required.')
      .isValidWeightString('Please verify your weight has been entered correctly.', 78, 395),
    tobacco: string().required('Tobacco usage is required.'),
  });
};

AboutMeForm.DefaultFormState = (prefill = false, basicInfo = null) => {
  return {
    gender: prefill ? 'M' : (basicInfo && basicInfo.gender) || '',
    birthdate: prefill ? '11/15/1980' : (basicInfo && basicInfo.birthdate) || '',
    tobacco: prefill ? 'No' : (basicInfo && basicInfo.tobacco) || '',
    state: prefill ? 'ID' : (basicInfo && basicInfo.state) || '',
    height: prefill ? '5 ft 10 in' : (basicInfo && basicInfo.height) || '',
    weight: prefill ? '175 pounds' : (basicInfo && basicInfo.weight) || '',
    usResident: true,
    bmi: (basicInfo && basicInfo.bmi) || undefined,
  };
};

export default AboutMeForm;
